/**
 * Badge types (moderator) — `GET/POST/PATCH/DELETE /verification/moderator/badges/`.
 *
 * Badges are data (product rule 5): a label, a rank and the evidence facts it
 * requires. Any moderator reads them; super moderators create and edit. A key
 * is fixed once awarded, and an awarded badge can only be deactivated.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { getApiErrorMessage } from "@/lib/apiError";
import type { EvidenceFact, VerificationBadge } from "@/services/moderators/generated/model";
import {
  getVerificationModeratorBadgesListQueryKey,
  useVerificationModeratorBadgesCreate,
  useVerificationModeratorBadgesDestroy,
  useVerificationModeratorBadgesFactsList,
  useVerificationModeratorBadgesList,
  useVerificationModeratorBadgesPartialUpdate,
} from "@/services/moderators/generated/moderators-verification/moderators-verification";
import "../ModeratorPage.css";
import "./Kyb.css";

interface BadgeDraft {
  key: string;
  label: string;
  description: string;
  rank: number;
  requiredEvidence: string[];
  isActive: boolean;
}

const EMPTY_DRAFT: BadgeDraft = { key: "", label: "", description: "", rank: 0, requiredEvidence: [], isActive: true };

const requiredOf = (badge: VerificationBadge): string[] =>
  Array.isArray(badge.requiredEvidence) ? (badge.requiredEvidence as string[]) : [];

const ModeratorKybBadgesPage = () => (
  <ModeratorLayout>{(moderator) => <Content isSuper={moderator.role === "SUPERMODERATOR"} />}</ModeratorLayout>
);

const Content = ({ isSuper }: { isSuper: boolean }) => {
  const { t } = useTranslation(["kyb", "common"]);
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useVerificationModeratorBadgesList();
  const { data: factsData } = useVerificationModeratorBadgesFactsList();
  const badges = (data as VerificationBadge[] | undefined) ?? [];
  const facts = (factsData as EvidenceFact[] | undefined) ?? [];
  const factLabel = (key: string) => facts.find((f) => f.key === key)?.label ?? key;
  const [editing, setEditing] = useState<number | "new" | null>(null);

  const invalidate = () => {
    setEditing(null);
    queryClient.invalidateQueries({ queryKey: getVerificationModeratorBadgesListQueryKey() });
  };

  return (
    <div>
      <div className="mod-page__toolbar">
        <Link to="/moderator/kyb" className="mod-page__btn mod-page__btn--ghost">
          {t("company.back")}
        </Link>
      </div>
      <header className="mod-page__header">
        <div>
          <h1 className="mod-page__title">{t("badgeTypes.title")}</h1>
          <p className="mod-page__subtitle">{t("badgeTypes.subtitle")}</p>
        </div>
        {isSuper ? (
          <button type="button" className="mod-page__btn mod-page__btn--primary" onClick={() => setEditing("new")}>
            {t("badgeTypes.add")}
          </button>
        ) : null}
      </header>

      <section className="kyb-section">
        <h2 className="kyb-section__title">{t("badgeTypes.factsTitle")}</h2>
        <p className="kyb-section__help">{t("badgeTypes.factsHelp")}</p>
        <ul className="kyb-facts">
          {facts.map((f) => (
            <li key={f.key} className="kyb-fact">
              <span className="mod-page__chip">{f.key}</span> {f.label}
            </li>
          ))}
        </ul>
      </section>

      {editing === "new" ? (
        <BadgeForm facts={facts} initial={EMPTY_DRAFT} onDone={invalidate} onCancel={() => setEditing(null)} />
      ) : null}

      {isLoading ? <div className="mod-page__loading">{t("common:states.loading")}</div> : null}
      {error ? <div className="mod-page__error">{t("badgeTypes.loadError")}</div> : null}
      {!isLoading && badges.length === 0 ? (
        <div className="kyb-callout kyb-callout--warning">{t("badges.noneSeeded")}</div>
      ) : null}

      {badges.map((badge) =>
        editing === badge.id ? (
          <BadgeForm
            key={badge.id}
            facts={facts}
            badgeId={badge.id}
            keyLocked={badge.awardCount > 0}
            initial={{
              key: badge.key,
              label: badge.label,
              description: badge.description ?? "",
              rank: badge.rank ?? 0,
              requiredEvidence: requiredOf(badge),
              isActive: badge.isActive ?? true,
            }}
            onDone={invalidate}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <article key={badge.id} className="kyb-section">
            <div className="kyb-provider__head">
              <div>
                <h2 className="kyb-section__title">
                  <i className="fa-solid fa-shield-halved" /> {badge.label}
                  <span className="mod-page__chip">{badge.key}</span>
                  <span className="mod-page__chip">{t("badgeTypes.rank", { rank: badge.rank ?? 0 })}</span>
                  {badge.isActive ? null : <span className="mod-page__chip">{t("badgeTypes.inactive")}</span>}
                </h2>
                {badge.description ? <p className="kyb-section__help">{badge.description}</p> : null}
                <div className="kyb-provider__tags">
                  <span className="kyb-note">{t("badgeTypes.requires")}</span>
                  {requiredOf(badge).map((f) => (
                    <span key={f} className="mod-page__chip mod-page__chip--success">
                      {factLabel(f)}
                    </span>
                  ))}
                </div>
                <p className="kyb-note">{t("badgeTypes.awardCount", { count: badge.awardCount })}</p>
              </div>
              {isSuper ? (
                <div className="mod-page__form-row">
                  <button
                    type="button"
                    className="mod-page__btn mod-page__btn--ghost"
                    onClick={() => setEditing(badge.id)}
                  >
                    {t("common:actions.edit")}
                  </button>
                  {badge.awardCount === 0 ? <DeleteBadge id={badge.id} onDone={invalidate} /> : null}
                </div>
              ) : null}
            </div>
          </article>
        ),
      )}
    </div>
  );
};

const DeleteBadge = ({ id, onDone }: { id: number; onDone: () => void }) => {
  const { t } = useTranslation(["kyb", "common"]);
  const destroy = useVerificationModeratorBadgesDestroy({ mutation: { onSuccess: onDone } });
  return (
    <>
      <button
        type="button"
        className="mod-page__btn mod-page__btn--danger"
        disabled={destroy.isPending}
        onClick={() => destroy.mutate({ id })}
      >
        {destroy.isPending ? t("common:actions.deleting") : t("common:actions.delete")}
      </button>
      {destroy.isError ? (
        <span className="mod-page__inline-error">{getApiErrorMessage(destroy.error, t("badgeTypes.deleteError"))}</span>
      ) : null}
    </>
  );
};

const BadgeForm = ({
  facts,
  initial,
  badgeId,
  keyLocked = false,
  onDone,
  onCancel,
}: {
  facts: EvidenceFact[];
  initial: BadgeDraft;
  badgeId?: number;
  keyLocked?: boolean;
  onDone: () => void;
  onCancel: () => void;
}) => {
  const { t } = useTranslation(["kyb", "common"]);
  const [draft, setDraft] = useState<BadgeDraft>(initial);
  const create = useVerificationModeratorBadgesCreate({ mutation: { onSuccess: onDone } });
  const update = useVerificationModeratorBadgesPartialUpdate({ mutation: { onSuccess: onDone } });
  const pending = create.isPending || update.isPending;
  const mutationError = create.error ?? update.error;

  const toggleFact = (key: string) =>
    setDraft((d) => ({
      ...d,
      requiredEvidence: d.requiredEvidence.includes(key)
        ? d.requiredEvidence.filter((k) => k !== key)
        : [...d.requiredEvidence, key],
    }));

  const save = () => {
    const payload = {
      key: draft.key.trim(),
      label: draft.label.trim(),
      description: draft.description.trim(),
      rank: draft.rank,
      requiredEvidence: draft.requiredEvidence,
      isActive: draft.isActive,
    };
    if (badgeId) update.mutate({ id: badgeId, data: payload });
    else create.mutate({ data: payload });
  };

  return (
    <section className="kyb-section kyb-form">
      <h2 className="kyb-section__title">{badgeId ? t("badgeTypes.editTitle") : t("badgeTypes.newTitle")}</h2>
      <div className="kyb-form__grid">
        <label>
          {t("badgeTypes.fields.key")}
          <input
            className="mod-page__input"
            value={draft.key}
            disabled={keyLocked}
            onChange={(e) => setDraft({ ...draft, key: e.target.value })}
          />
          {keyLocked ? <span className="kyb-note">{t("badgeTypes.keyLocked")}</span> : null}
        </label>
        <label>
          {t("badgeTypes.fields.label")}
          <input
            className="mod-page__input"
            value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
          />
        </label>
        <label>
          {t("badgeTypes.fields.rank")}
          <input
            type="number"
            min={0}
            className="mod-page__input"
            value={draft.rank}
            onChange={(e) => setDraft({ ...draft, rank: Number(e.target.value) || 0 })}
          />
        </label>
        <label>
          {t("badgeTypes.fields.active")}
          <select
            className="mod-page__select"
            value={draft.isActive ? "yes" : "no"}
            onChange={(e) => setDraft({ ...draft, isActive: e.target.value === "yes" })}
          >
            <option value="yes">{t("common:labels.yes")}</option>
            <option value="no">{t("common:labels.no")}</option>
          </select>
        </label>
      </div>
      <label>
        {t("badgeTypes.fields.description")}
        <textarea
          className="mod-page__input"
          rows={2}
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        />
      </label>
      <fieldset className="kyb-form">
        <legend className="kyb-sub__title">{t("badgeTypes.fields.requiredEvidence")}</legend>
        {facts.map((f) => (
          <label key={f.key} style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={draft.requiredEvidence.includes(f.key)}
              onChange={() => toggleFact(f.key)}
            />
            {f.label}
          </label>
        ))}
      </fieldset>
      <div className="mod-page__form-row">
        <button
          type="button"
          className="mod-page__btn mod-page__btn--primary"
          disabled={pending || !draft.key.trim() || !draft.label.trim() || draft.requiredEvidence.length === 0}
          onClick={save}
        >
          {pending ? t("common:actions.saving") : t("common:actions.save")}
        </button>
        <button type="button" className="mod-page__btn mod-page__btn--ghost" onClick={onCancel}>
          {t("common:actions.cancel")}
        </button>
      </div>
      {mutationError ? (
        <div className="mod-page__inline-error">{getApiErrorMessage(mutationError, t("badgeTypes.saveError"))}</div>
      ) : null}
    </section>
  );
};

export default ModeratorKybBadgesPage;
